import { MessageTypes } from "../constants/enums";
import { TypeCount } from "components/Dashboard/MessageTypes/GlobalFilterBar";

/**
 * Tier-1 buckets, shared by the two panels that now disagree about nothing.
 *
 * These used to live inside MessagePanel, which was fine while it was the only
 * reader. The map column now has to answer the same question — "which bucket is
 * active?" — and two copies of this table would drift the first time a bucket
 * was added to one of them.
 *
 * The values are the message types the index actually stores — the labels are
 * the only thing softened for readers. Counts are omitted: the stream is
 * paginated, so a real per-type total would need its own aggregation query.
 */
export const PAGE_TYPE_BUCKETS: TypeCount[] = [
  { key: "all", label: "Map" },
  { key: "people", label: "People" },
  { key: "media", label: "Media" },
  { key: "files", label: "Files" },
  { key: "pay", label: "Payments" },
  { key: "event", label: "Events" },
  { key: "rsvp", label: "RSVP" },
  { key: "survey", label: "Surveys" },
  { key: "place", label: "Places" },
  { key: "gps-report", label: "GPS reports" },
  { key: "alert", label: "Alerts" },
  { key: "ai", label: "AI" },
  // The device buckets close the row. They are the ones a reader scrolls to
  // deliberately rather than sweeps past, and grouping them keeps the live
  // feeds together at one end instead of scattered through the page types.
  { key: "gps", label: "GPS live" },
  { key: "cam", label: "Cameras" },
  { key: "rx", label: "RX" },
];

/**
 * A bucket is not always one type. RSVP only makes sense with the replies that
 * answer it, and an alert rule and a fired alert belong in the same list — so a
 * pill maps to the whole set of types its view needs to read.
 */
export const BUCKET_TYPES: Record<string, string[]> = {
  rsvp: [MessageTypes.Rsvp, MessageTypes.RsvpResponse],
  alert: [MessageTypes.Alert, MessageTypes.AlertConfiguration],
  // Files and AI are two more readings of the same media messages — the split
  // is by MIME type and by whether analysis exists, not by a separate type.
  files: [MessageTypes.Media],
  ai: [MessageTypes.Media],
  // People come from the page's member list rather than from messages at
  // all, so this bucket names no type.
  people: [],
  // The camera view is the one place a fired alert has to be read next to the
  // device that raised it — that is what puts the flag on a tile — so the
  // bucket reads both. Alert *rules* stay out: a setting is not an incident.
  cam: [MessageTypes.Cam, MessageTypes.Alert],
};

/**
 * Buckets whose result *is* a map, so the map column keeps the map rather than
 * being replaced by a card view.
 *
 * `all` is in here because an unfiltered page is the default view the map was
 * built for — the client's wording, "no filter", is that same state.
 *
 * `""` is the funnel popup picking a combination of types that matches no
 * single pill. There is no one ledger view for "media and payments", so there
 * is nothing to put in the column — taking a working map away to replace it
 * with a "pick one type" placeholder would be a straight downgrade.
 */
export const MAP_BUCKETS = ["all", "", "place", "gps", "gps-report"];

/** Whether the map column should show the map rather than a filtered result. */
export const isMapBucket = (bucket: string): boolean =>
  MAP_BUCKETS.indexOf(bucket || "all") !== -1;

export const typesForBucket = (bucket: string): string[] =>
  bucket === "all" ? [] : BUCKET_TYPES[bucket] || [bucket];

/** Same set of types, order-insensitive — the query joins them with `|`. */
export const sameTypes = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((type) => b.indexOf(type) !== -1);

/**
 * Which pill is lit is derived from the filter itself rather than tracked
 * separately, so the funnel popup and the pill row cannot disagree.
 */
export const bucketForTypes = (types: string[]): string => {
  if (!types || types.length === 0) return "all";
  const match = PAGE_TYPE_BUCKETS.find((bucket) => {
    const wanted = typesForBucket(bucket.key);
    return (
      wanted.length === types.length &&
      wanted.every((type) => types.indexOf(type) !== -1)
    );
  });
  // A hand-picked combination from the funnel matches no single pill; that is
  // a real state, so nothing is lit rather than lighting the closest guess.
  return match ? match.key : "";
};

/**
 * Several pills share one query — Media, Files and AI all read `media`
 * messages — so the filter alone can no longer say which view was asked for.
 * The clicked pill is remembered, but only wins while it still agrees with the
 * filter in the store; changing types from the funnel popup hands control
 * straight back to the derived value.
 */
export const resolveActiveBucket = (
  pickedBucket: string,
  messageFilter: string[]
): string =>
  sameTypes(typesForBucket(pickedBucket), messageFilter || [])
    ? pickedBucket
    : bucketForTypes(messageFilter || []);
